// shared/src/oracle/relations-store.ts
// D1 access for `oracle_token_relations` (migration 0095) — the persisted wrapped↔native
// corroboration series (§3d).
//
// Row/Zod/writer/reader live together and ship with the DDL in ONE commit
// (feedback_writer_ddl_lockstep — the 2026-05-25/26 incident class).
//
// The table records EVERY check, not just breaches: a clean check that leaves no trace is
// indistinguishable from a check that never ran, and the peg's drift over time is the
// actual signal ("is $WSTETH's tracking error growing?"), which breach-only logs cannot answer.

import { z } from 'zod';
import type { RelationCheck } from './token-relations.js';

export interface OracleTokenRelationRow {
  readonly id:             string;
  readonly run_id:         string;
  readonly key:            string;
  readonly native_key:     string;
  readonly key_value:      number | null;
  readonly native_value:   number | null;
  readonly expected_ratio: number;
  readonly observed_ratio: number;
  readonly deviation_pct:  number;
  readonly tol:            number;
  readonly breached:       number;   // 0/1 — SQLite has no bool
  readonly ratio_source:   string;
  readonly observed_at:    string | null;
  readonly created_at:     string;
}

export const OracleTokenRelationRowSchema = z.object({
  id:             z.string().min(1),
  run_id:         z.string().min(1),
  key:            z.string().min(1),
  native_key:     z.string().min(1),
  key_value:      z.number().finite().nullable(),
  native_value:   z.number().finite().nullable(),
  expected_ratio: z.number().finite().positive(),
  observed_ratio: z.number().finite().positive(),
  deviation_pct:  z.number().finite(),
  tol:            z.number().finite().positive(),
  breached:       z.number().int().min(0).max(1),
  ratio_source:   z.string().min(1),
  observed_at:    z.string().nullable(),
  created_at:     z.string().min(1),
});

/** Minimal D1 surface (mirrors the sibling oracle stores — keeps this unit testable). */
export interface RelationsD1 {
  prepare(sql: string): {
    bind(...values: unknown[]): {
      run(): Promise<unknown>;
      all<T>(): Promise<{ results?: T[] }>;
    };
  };
}

/** Deterministic id — one row per (run, key). Re-running a fire OVERWRITES rather than
 *  duplicating (feedback_idempotent_writes_under_retry). The identity keys the LOGICAL
 *  item (this run's check of this key), never a per-call value. */
export function makeRelationId(runId: string, key: string): string {
  return `${runId}:${key}`;
}

export function relationCheckToRow(
  check:      RelationCheck,
  runId:      string,
  keyValue:   number | null,
  nativeValue: number | null,
  observedAt: string | null,
  createdAt:  string,
): OracleTokenRelationRow {
  return {
    id:             makeRelationId(runId, check.key),
    run_id:         runId,
    key:            check.key,
    native_key:     check.nativeKey,
    key_value:      keyValue,
    native_value:   nativeValue,
    expected_ratio: check.expectedRatio,
    observed_ratio: check.observedRatio,
    deviation_pct:  check.deviationPct,
    tol:            check.tol,
    breached:       check.breached ? 1 : 0,
    ratio_source:   check.ratioLabel,
    observed_at:    observedAt,
    created_at:     createdAt,
  };
}

const COLS =
  'id, run_id, key, native_key, key_value, native_value, expected_ratio, observed_ratio, deviation_pct, tol, breached, ratio_source, observed_at, created_at';

/** Idempotent upsert — a replayed fire rewrites its own row instead of duplicating. */
export async function writeRelations(db: RelationsD1, rows: ReadonlyArray<OracleTokenRelationRow>): Promise<void> {
  for (const r of rows) {
    await db
      .prepare(
        `INSERT INTO oracle_token_relations (${COLS})
         VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?)
         ON CONFLICT(id) DO UPDATE SET
           key_value=excluded.key_value, native_value=excluded.native_value,
           expected_ratio=excluded.expected_ratio, observed_ratio=excluded.observed_ratio,
           deviation_pct=excluded.deviation_pct, tol=excluded.tol,
           breached=excluded.breached, ratio_source=excluded.ratio_source,
           observed_at=excluded.observed_at, created_at=excluded.created_at`,
      )
      .bind(
        r.id, r.run_id, r.key, r.native_key, r.key_value, r.native_value,
        r.expected_ratio, r.observed_ratio, r.deviation_pct, r.tol,
        r.breached, r.ratio_source, r.observed_at, r.created_at,
      )
      .run();
  }
}

/** The CURRENTLY-WATCHED relations — every key checked in the LATEST fire (one row per key,
 *  since the writer keys one row per (run_id, key)). Scoping to the latest run_id — rather than
 *  a GROUP BY key over ALL history — means a RETIRED relation, one whose producer stopped
 *  writing rows (e.g. $WSTETH after #66), drops out within one cadence instead of lingering on
 *  its stale MAX(created_at) for the whole retention window. All live relations fire together
 *  each cadence, so the latest run IS the live watched set; a key absent from it is either
 *  retired or was skipped this fire (self-heals next cadence), and its "current peg" is
 *  genuinely unknown right now. The drift series + breach log read their own windows. */
export async function readLatestRelations(db: RelationsD1): Promise<OracleTokenRelationRow[]> {
  const res = await db
    .prepare(
      `SELECT ${COLS}
         FROM oracle_token_relations
        WHERE run_id = (SELECT run_id FROM oracle_token_relations ORDER BY created_at DESC LIMIT 1)
        ORDER BY key ASC`,
    )
    .bind()
    .all<OracleTokenRelationRow>();
  return res.results ?? [];
}

/** A key's series over the last N days — the drift chart. C018: build the cutoff with
 *  strftime('%Y-%m-%dT%H:%M:%fZ','now',…), never a bare space-form now() cutoff (space-form
 *  sorts BELOW ISO-Z → a silently empty window). */
export async function readRelationSeries(db: RelationsD1, key: string, days: number): Promise<OracleTokenRelationRow[]> {
  const res = await db
    .prepare(
      `SELECT ${COLS}
         FROM oracle_token_relations
        WHERE key = ? AND created_at > strftime('%Y-%m-%dT%H:%M:%fZ','now', ?)
        ORDER BY created_at ASC`,
    )
    .bind(key, `-${days} days`)
    .all<OracleTokenRelationRow>();
  return res.results ?? [];
}

/** The breach event log — every row where the observed peg exceeded its tolerance, newest
 *  first, capped. A breach is rare and audit-worthy (a wrapped token diverging from its
 *  native reference), so unlike the drift series this is surfaced as a discrete event list. */
export async function readRecentBreaches(db: RelationsD1, limit: number): Promise<OracleTokenRelationRow[]> {
  const res = await db
    .prepare(
      `SELECT ${COLS}
         FROM oracle_token_relations
        WHERE breached = 1
        ORDER BY created_at DESC
        LIMIT ?`,
    )
    .bind(limit)
    .all<OracleTokenRelationRow>();
  return res.results ?? [];
}

// Retention: purged at RELATIONS_RETENTION_DAYS (90d, matching canonical — this is a
// canonical-grade series, not raw vendor chatter) inside oracle-worker's existing
// best-effort purge BATCH, alongside oracle_observations + oracle_canonical. It lives
// there rather than as a function here so all three tables share ONE D1 round-trip; a
// purgeRelations() helper in this module would have exactly one caller and would have to
// break that batch to be used.
